import { useId, type InputHTMLAttributes, type Ref } from "react";
import { cn } from "@/lib/utils";
import { fieldClass } from "./styles";

function describedByIds(id: string, hint?: string, error?: string) {
  return [error ? `${id}-error` : undefined, !error && hint ? `${id}-hint` : undefined]
    .filter(Boolean)
    .join(" ");
}

export type InputProps = {
  label: string;
  hideLabel?: boolean;
  hint?: string;
  error?: string;
  ref?: Ref<HTMLInputElement>;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "size">;

export function Input({
  label,
  hideLabel = false,
  hint,
  error,
  id,
  className,
  ref,
  ...props
}: InputProps) {
  const generated = useId();
  const fieldId = id ?? generated;
  const describedBy = describedByIds(fieldId, hint, error);

  return (
    <div className="grid gap-2" data-slot="field">
      <label htmlFor={fieldId} className={cn("text-sm text-foreground", hideLabel && "sr-only")}>
        {label}
      </label>
      <input
        ref={ref}
        id={fieldId}
        data-slot="input"
        data-field="true"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        className={cn(fieldClass, error && "border-danger", className)}
        {...props}
      />
      <FieldMessage id={fieldId} hint={hint} error={error} />
    </div>
  );
}

export function FieldMessage({ id, hint, error }: { id: string; hint?: string; error?: string }) {
  if (error) {
    return (
      <p id={`${id}-error`} role="alert" className="text-sm text-danger">
        {error}
      </p>
    );
  }
  if (!hint) return null;
  return (
    <p id={`${id}-hint`} className="text-sm text-muted">
      {hint}
    </p>
  );
}

